<template>
  <div class="container-fluid">
    <div class="loading" v-if="loading">Loading&#8230;</div>

    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Categorías de productos</h1>
          <p class="view-header__subtitle">Organizá las categorías y subcategorías usadas en el inventario.</p>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-search"></i>
          Buscador
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="filters.qname" class="form-control" placeholder="Buscar categoría...">
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- ARBOL -->
      <div class="col-md-8">
        <div class="data-table">
          <div class="table-responsive">
            <table class="table mb-0">
              <thead>
                <tr>
                  <th>
                    <a href="#" @click.prevent="sortList('name')" class="data-table__sort" :class="{ 'is-active': filters.sorting.col === 'name' }">
                      Categoría
                      <i :class="sortIcon('name')"></i>
                    </a>
                  </th>
                  <th class="text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="node in flatCategoryList" :key="node.id">
                  <td>
                    <div class="category-row" :style="{ paddingLeft: (node.level * 24) + 'px' }">
                      <button
                        v-if="node.hasChildren"
                        type="button"
                        class="category-expander"
                        :class="{ 'is-open': isExpanded(node.id) }"
                        @click="toggleNode(node.id)"
                        :aria-expanded="isExpanded(node.id) ? 'true' : 'false'"
                      ></button>
                      <span v-else class="category-expander-spacer" aria-hidden="true"></span>
                      <a href="#" class="data-table__primary category-link" @click.prevent="link(node.id)">{{ node.label }}</a>
                    </div>
                  </td>
                  <td class="text-right">
                    <div class="table-action-group">
                      <button class="table-action-btn" type="button" title="Editar" @click="getCategory(node)">
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button class="table-action-btn" type="button" title="Eliminar" @click="deleteCategory(node)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!flatCategoryList.length">
                  <td colspan="2" class="data-table__empty">No se encontraron categorías</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- PAGINACION -->
          <div class="data-table__footer">
            <div class="data-table__info">
              Mostrando {{ totalItems === 0 ? 0 : (filters.page - 1) * filters.itemsPerPage + 1 }}
              a {{ Math.min(filters.page * filters.itemsPerPage, totalItems) }}
              de {{ totalItems }} items.
            </div>

            <ul class="data-table__pagination">
              <li class="page-item" :class="{ 'disabled': filters.page === 1 }">
                <a class="page-link" href="#" @click.prevent="prevPage()">
                  <i class="bi bi-chevron-left"></i>
                </a>
              </li>
              <li v-if="totalPages > pageInterval && (filters.page - (pageInterval / 2)) > 1" class="page-item">
                <a class="page-link" href="#" @click.prevent="jumpPrevBlock()">...</a>
              </li>
              <template v-for="index in totalPages">
                <li :key="index" v-if="inIntervalPage(index)" class="page-item" :class="{ 'active': filters.page === index }">
                  <a class="page-link" href="#" @click.prevent="changePage(index)">{{ index }}</a>
                </li>
              </template>
              <li v-if="totalPages > pageInterval && (filters.page + (pageInterval / 2)) < totalPages" class="page-item">
                <a class="page-link" href="#" @click.prevent="jumpNextBlock()">...</a>
              </li>
              <li class="page-item" :class="{ 'disabled': filters.page >= totalPages }">
                <a class="page-link" href="#" @click.prevent="nextPage()">
                  <i class="bi bi-chevron-right"></i>
                </a>
              </li>
            </ul>

            <div class="data-table__page-size">
              <q-select v-model.number="filters.itemsPerPage" :options="itemsPerPageOptions" />
            </div>
          </div>
        </div>
      </div>

      <!-- FORMULARIO -->
      <div class="col-md-4">
        <div class="form-card">
          <div class="form-card__header">
            <h6 class="form-card__title">
              <i :class="category.id == null ? 'bi bi-plus-lg' : 'bi bi-pencil'"></i>
              {{ category.id == null ? 'Nueva categoría' : 'Editar categoría' }}
            </h6>
            <button v-if="category.id != null" type="button" class="form-card__btn" @click="resetForm()">
              <i class="bi bi-plus-lg"></i>
              Nueva
            </button>
          </div>
          <div class="form-card__body">
            <form @submit.prevent>
              <div class="form-card__field">
                <label class="form-card__label" for="category-name">Nombre</label>
                <input
                  id="category-name"
                  v-model.trim="category.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': nameState === false }"
                  placeholder="Nombre de la categoría"
                  aria-describedby="category-name-feedback"
                />
                <div v-if="nameState === false" id="category-name-feedback" class="form-card__error">
                  El nombre debe tener un mínimo de 3 letras
                </div>
                <div v-else class="form-card__hint">Campo obligatorio</div>
              </div>
              <div class="form-card__field">
                <label class="form-card__label" for="category-parent">Categoría padre</label>
                <q-select v-model="category.parent_id" :options="validParentOptions" placeholder="Ninguna" />
              </div>
            </form>
          </div>
          <div class="form-card__footer">
            <button type="button" class="form-card__btn form-card__btn--primary" :disabled="!canSave || loading" @click="saveCategory()">
              <i class="bi bi-check-lg"></i>
              Guardar categoría
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import QSelect from '@/components/shared/QSelect.vue';

const emptyCategory = () => ({ id: null, name: '', parent_id: null });

export default {
  components: { QSelect },
  data: function() {
    return {
      loading: false,
      timeoutId: null,
      totalPages: 1,
      pageInterval: 16,
      totalItems: 0,
      categoryTree: [],
      category: emptyCategory(),
      expandedIds: {},
      filters: {
        qname: '',
        page: 1,
        itemsPerPage: 15,
        sorting: { col: '', order: 'desc' },
      },
    };
  },
  mounted() {
    this.getData();
  },
  computed: {
    ...mapState('categories', { categories: state => state.categories }),
    itemsPerPageOptions() {
      return [
        { value: 15, label: '15 por página' },
        { value: 25, label: '25 por página' },
        { value: 50, label: '50 por página' },
      ];
    },
    validParentOptions() {
      return this.validParents.map(c => ({ value: c.id, label: c.name }));
    },
    categoryList() {
      return Array.isArray(this.categories) ? this.categories : [];
    },
    flatCategoryList() {
      const flat = [];
      const walk = (items, level) => {
        for (const item of items) {
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;
          flat.push({ ...item, level, hasChildren });
          if (hasChildren && this.expandedIds[item.id]) {
            walk(item.children, level + 1);
          }
        }
      };
      walk(Array.isArray(this.categoryTree) ? this.categoryTree : [], 0);
      return flat;
    },
    descendantIds() {
      const set = {};
      if (this.category.id == null) return set;
      const collectAll = (items) => {
        for (const item of items) {
          set[item.id] = true;
          if (Array.isArray(item.children)) collectAll(item.children);
        }
      };
      const findNode = (items) => {
        for (const item of items) {
          if (item.id === this.category.id) {
            if (Array.isArray(item.children)) collectAll(item.children);
            return true;
          }
          if (Array.isArray(item.children) && findNode(item.children)) return true;
        }
        return false;
      };
      findNode(Array.isArray(this.categoryTree) ? this.categoryTree : []);
      return set;
    },
    validParents() {
      return this.categoryList.filter(c => c.id !== this.category.id && !this.descendantIds[c.id]);
    },
    nameState() {
      const len = this.category.name ? this.category.name.length : 0;
      if (len === 0) return null;
      return len >= 3 ? null : false;
    },
    canSave() {
      return !!(this.category.name && this.category.name.trim().length >= 3);
    },
  },
  created() {
    this.$store.dispatch('categories/getCategories');
  },
  watch: {
    'filters.qname': { handler() { this.getDataWithDelay(); } },
    'filters.itemsPerPage': { handler() { this.filters.page = 1; this.getData(); } },
  },
  methods: {
    getData() {
      this.loading = true;
      axios.post('/api/categories/list', this.filters)
        .then(response => {
          if (response.data.success) {
            this.categoryTree = response.data.data.categories;
            this.totalPages = response.data.data.totalPages;
            this.totalItems = response.data.data.totalItems;
          } else {
            this.categoryTree = [];
            this.totalPages = 1;
            this.totalItems = 0;
          }
        })
        .catch(error => {
          console.error('getData:', error);
          alert('No se pudieron cargar las categorías.');
        })
        .finally(() => { this.loading = false; });
    },

    refreshData() {
      this.getData();
      this.$store.dispatch('categories/getCategories');
    },

    resetForm() {
      this.category = emptyCategory();
    },

    getCategory(data) {
      if (this.loading) return;
      this.loading = true;
      this.$http.get('/api/categories/getCategory/' + data.id)
        .then(response => {
          this.category = response.data.data;
        })
        .catch(error => {
          console.error('getCategory:', error);
          alert('No se pudo cargar la categoría.');
        })
        .finally(() => { this.loading = false; });
    },

    deleteCategory(data) {
      if (this.loading) return;
      if (!confirm('¿Está seguro que desea borrar esta categoría?')) return;
      this.loading = true;
      this.$http.post('/api/categories/delete', { id: data.id })
        .then(response => {
          if (response.data.code == 201) {
            if (this.category.id === data.id) this.resetForm();
            this.refreshData();
          } else {
            alert('No se pudo borrar la categoría.');
          }
        })
        .catch(error => {
          console.error('deleteCategory:', error);
          alert('No se pudo borrar la categoría.');
        })
        .finally(() => { this.loading = false; });
    },

    saveCategory() {
      if (!this.canSave || this.loading) return;
      this.loading = true;
      const req = this.category.id != null
        ? this.$http.post('/api/categories/edit/' + this.category.id, this.category)
        : this.$http.post('/api/categories/add', this.category);
      req.then(response => {
        if (response.data && response.data.code == 201) {
          this.resetForm();
          this.refreshData();
        } else {
          alert('No se pudo guardar la categoría.');
        }
      })
      .catch(error => {
        console.error('saveCategory:', error);
        alert('No se pudo guardar la categoría.');
      })
      .finally(() => { this.loading = false; });
    },

    toggleNode(id) {
      this.expandedIds = { ...this.expandedIds, [id]: !this.expandedIds[id] };
    },
    isExpanded(id) {
      return !!this.expandedIds[id];
    },

    getDataWithDelay() {
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.filters.page = 1;
        this.getData();
      }, 1500);
    },

    link(id) {
      this.$router.push({ name: 'Inventario', params: { categoryId: id } });
    },

    sortList(col) {
      if (this.filters.sorting.col === col) {
        this.filters.sorting.order = this.filters.sorting.order === 'asc' ? 'desc' : 'asc';
      } else {
        this.filters.sorting.col = col;
        this.filters.sorting.order = 'asc';
      }
      this.filters.page = 1;
      this.getData();
    },
    sortIcon(col) {
      if (this.filters.sorting.col !== col) return 'bi bi-chevron-expand';
      return this.filters.sorting.order === 'asc' ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
    },

    prevPage() {
      if (this.filters.page > 1) {
        this.filters.page--;
        this.getData();
      }
    },
    nextPage() {
      if (this.filters.page < this.totalPages) {
        this.filters.page++;
        this.getData();
      }
    },
    jumpPrevBlock() {
      this.filters.page = Math.max(1, this.filters.page - this.pageInterval);
      this.getData();
    },
    jumpNextBlock() {
      this.filters.page = Math.min(this.totalPages, this.filters.page + this.pageInterval);
      this.getData();
    },
    changePage(page) {
      this.filters.page = page;
      this.getData();
    },
    inIntervalPage(index) {
      if (this.totalPages <= this.pageInterval) return true;
      const half = Math.floor(this.pageInterval / 2);
      let limitprev = this.filters.page - half;
      let limitnext = this.filters.page + half;
      if (limitprev < 1) { limitprev = 1; limitnext = this.pageInterval; }
      if (limitnext > this.totalPages) {
        limitnext = this.totalPages;
        limitprev = this.totalPages - this.pageInterval + 1;
      }
      return index >= limitprev && index <= limitnext;
    },
  },
};
</script>

<style scoped>
.category-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-link {
  color: inherit;
  text-decoration: none;
}
.category-link:hover {
  color: #0d6efd;
  text-decoration: none;
}

.category-expander,
.category-expander-spacer {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.category-expander {
  position: relative;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--surface);
  color: var(--text-soft);
  padding: 0;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.category-expander:hover,
.category-expander:focus {
  background: var(--surface-subtle);
  border-color: #b8bfc9;
  color: var(--text);
  outline: none;
}
.category-expander:before,
.category-expander:after {
  content: '';
  position: absolute;
  background: currentColor;
  display: block;
}
.category-expander:before {
  width: 8px;
  height: 2px;
  left: calc(50% - 4px);
  top: calc(50% - 1px);
}
.category-expander:after {
  width: 2px;
  height: 8px;
  left: calc(50% - 1px);
  top: calc(50% - 4px);
  transition: transform 0.15s;
}
.category-expander.is-open:after {
  transform: rotate(90deg);
}
</style>
